<template>
  <div class="quiz">
    <!-- Dynamically bind component to value of `currentStep` -->
    <b-progress :value="stepProgress" :max="stepMax"></b-progress>
    <transition name="slide-fade" mode="out-in" :duration="transitionDuration">
      <component v-bind:is="currentStep" v-bind:selection=selection v-bind:user=user v-bind:lists=lists @back=previous @completed=next @finished=finish></component>
    </transition>
  </div>
</template>

<script>
import CareerInterestStep from './steps/CareerInterestStep.vue'
import CareerInterstitial from './steps/CareerInterstitial.vue'
import MajorInterstitial from './steps/MajorInterstitial.vue'
import CareerKnownStep from './steps/CareerKnownStep.vue'
import CareerMajorStep from './steps/CareerMajorStep.vue'
import CareerSelectStep from './steps/CareerSelectStep.vue'
import PathSelectStep from './steps/PathSelectStep.vue'
import SchoolInterstitial from './steps/SchoolInterstitial.vue'
import SchoolKnownStep from './steps/SchoolKnownStep.vue'
import SchoolStateStep from './steps/SchoolStateStep.vue'
import SchoolSelectStep from './steps/SchoolSelectStep.vue'
import SchoolTypeStep from './steps/SchoolTypeStep.vue'
import LastInterstitial from './steps/LastInterstitial.vue'

const DEFAULT_USER_PATH = 'school';
const FIRST_STEP = PathSelectStep;

// the three user paths, and their respective steps in order (callback returns step function or `null` if skipped)
const paths = {
  'school': [
    () => SchoolInterstitial,
    () => SchoolKnownStep,
    (user) => (user.schoolKnown === 'yes') ? SchoolSelectStep : null,
    (user) => (user.schoolKnown === 'no') ? SchoolTypeStep : null,
    (user) => (user.schoolKnown === 'no') ? SchoolStateStep : null,
    () => CareerInterstitial,
    () => CareerKnownStep,
    (user) => (user.careerKnown === 'yes') ? CareerSelectStep : null,
    (user) => (user.careerKnown === 'no') ? CareerMajorStep : null,
    (user) => (user.majorKnown === 'no') ? CareerInterestStep : null,
    () => LastInterstitial,
  ],
  'career': [
    () => CareerInterstitial,
    () => CareerKnownStep,
    (user) => (user.careerKnown === 'yes') ? CareerSelectStep : null,
    (user) => (user.careerKnown === 'no') ? CareerMajorStep : null,
    (user) => (user.majorKnown === 'no') ? CareerInterestStep : null,
    () => SchoolInterstitial,
    () => SchoolKnownStep,
    (user) => (user.schoolKnown === 'yes') ? SchoolSelectStep : null,
    (user) => (user.schoolKnown === 'no') ? SchoolTypeStep : null,
    (user) => (user.schoolKnown === 'no') ? SchoolStateStep : null,
    () => LastInterstitial,
  ],
  'major': [
    () => MajorInterstitial,
    () => CareerMajorStep,
    (user) => (user.majorKnown === 'no') ? CareerInterestStep : null,
    () => SchoolInterstitial,
    () => SchoolKnownStep,
    (user) => (user.schoolKnown === 'yes') ? SchoolSelectStep : null,
    (user) => (user.schoolKnown === 'no') ? SchoolTypeStep : null,
    (user) => (user.schoolKnown === 'no') ? SchoolStateStep : null,
    () => LastInterstitial,
  ],
}

let progress = -1; // index of path step (i.e., progress in quiz)

export default {
  name: 'Quiz',
	props: {
		lists: Object,
	},
  data: function () {
    return {
      user: {
        path: null, // which of the three paths user chose
        schoolKnown: null, // whether user has chosen a particular school
        careerKnown: null, // whether user has chosen a particular career
        majorKnown: null,
        schools: [], // IDs of schools selected
        career: null, // ID of career selected
        majors: [],
        skills: [],
        state: null,
        instate: null,
        occupation: null,
        type: null,
      },
      selection: {
        career: null,
      },
      currentStep: FIRST_STEP, // first step in quiz
      transitionDuration: null,
      stepProgress: 0,
      stepMax: 100
    }
  },
  methods: {
    /**
     * Bubble completion to `App` component to display results screen
     */
    finish: function () {
      this.$emit('finished', this.user);
    },
    /**
     * Move to previous step
     */
    previous: function () {
      // desired path user selected (`school`, `major`, `career`, `unknown`)
      const userPath = this.user.path;
      const effectiveUserPath = (userPath === 'unknown') ? DEFAULT_USER_PATH : userPath;

      // increment progress and retrieve next step
      let previousStep = (progress === 0) ? FIRST_STEP : paths[effectiveUserPath][--progress](this.user);

      // update progress bar
      this.stepProgress = Math.max((progress / paths[effectiveUserPath].length) * 100, 0);

      // some steps are skipped because of user decisions, and interstitials
      // are always skipped when going in reverse
      if (!previousStep || previousStep.name.indexOf('Interstitial') !== -1) {
        this.previous();
        return;
      }

      // set transition duration based on currentStep
      this.transitionDuration = this.currentStep.name.indexOf('Interstitial') !== -1 ? 1100 : 400;

      // scroll to top when changing steps
      window.setTimeout(() => window.scrollTo(0, 0), this.transitionDuration);

      // replace `currentStep` with new component
      this.currentStep = previousStep;
    },
    /**
     * Move to next step
     */
    next: function () {
      // desired path user selected (`school`, `major`, `career`, `unknown`)
      const userPath = this.user.path;
      const effectiveUserPath = (userPath === 'unknown') ? DEFAULT_USER_PATH : userPath;

      // check if complete
      if (paths[effectiveUserPath].length === progress + 1) {
        this.finish();
        return;
      }

      // increment progress and retrieve next step
      let nextStep = paths[effectiveUserPath][++progress](this.user);

      // update progress bar
      this.stepProgress = Math.max((progress / paths[effectiveUserPath].length) * 100, 0);

      // some steps are skipped because of user decisions
      if (!nextStep) {
        this.next();
        return;
      }

      // set transition duration based on currentStep
      this.transitionDuration = this.currentStep.name.indexOf('Interstitial') > 0 ? 1100 : 400;

      // scroll to top when changing steps
      window.setTimeout(() => window.scrollTo(0, 0), this.transitionDuration);

      // replace `currentStep` with new component
      this.currentStep = nextStep;
    }
  }
}
</script>

<style scoped>
</style>
